import androidx.compose.foundation.background
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.*
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.transitions.SlideTransition
import com.essntl.core.network.NetworkManager
import com.essntl.core.settings.domain.repository.ConfigurationRepository
import com.essntl.core.settings.domain.repository.FeatureRepository
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.utils.windowmanager.WindowManager
import com.essntl.features.authentication.domain.repository.AuthRepository
import com.essntl.features.currency.domain.repository.CurrencyRepository
import essntl.key.KeyEventManager
import essntl.key.LocalKeyEventManager
import com.essntl.toastmanager.ToastManager
import com.essntl.uikit.resources.LocalRes
import com.essntl.uikit.theme.EssntlTheme
import com.essntl.uikit.theme.esColorScheme
import core.config.CurrencyConfig
import core.config.FeatureConfig
import core.dateformat.domain.DateFormatUseCase
import essntl.components.EnvBanner
import essntl.components.EsToastContainer
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import navigation.root.auth.AuthScreenNav
import navigation.root.home.HomeScreenNav
import navigation.root.splash.SplashScreenNav
import org.koin.compose.KoinContext
import org.koin.compose.koinInject
import resources.EsResourcesImpl
import utils.checkAuth
import utils.checkException

@Composable
fun App(
    isConnected: Boolean? = null,
    screen: Screen? = null,
    openWindow: (Screen) -> Unit = {},
) {
    val focusRequester = remember { FocusRequester() }
    val keyEventManager = remember { KeyEventManager() }
    val isFocused = rememberSaveable { mutableStateOf(false) }
    val isLoading = rememberSaveable { mutableStateOf(false) }

    KoinContext {
        val toastManager = koinInject<ToastManager>()
        val networkManager = koinInject<NetworkManager>()
        val authRepository = koinInject<AuthRepository>()
        val featureRepository = koinInject<FeatureRepository>()
        val dateFormatUseCase = koinInject<DateFormatUseCase>()
        val configurationRepository = koinInject<ConfigurationRepository>()
        val currencyRepository = koinInject<CurrencyRepository>()
        val loadingManager = koinInject<LoadingManager>()

        val scope = rememberCoroutineScope()

        LaunchedEffect(isConnected) {
            networkManager.setState(isConnected)
            println("Connection status App: $isConnected")
        }

        LaunchedEffect(Unit) {
            loadingManager.isLoadingFlow.collect {
                isLoading.value = it
            }
        }

        if (screen == null) {
            val windowManager = koinInject<WindowManager>()
            LaunchedEffect(Unit) {
                windowManager.screenFlow.collect {
                    openWindow(it)
                }
            }
        }

        Box(
            modifier = Modifier
                .fillMaxSize(),
        ) {
            CompositionLocalProvider(
                LocalKeyEventManager provides keyEventManager,
                LocalRes provides EsResourcesImpl(),
            ) {
                EssntlTheme {
                    Box(
                        modifier =
                            Modifier
                                .onKeyEvent { event ->
                                    if (isFocused.value) {
                                        scope.launch {
                                            keyEventManager.emitEvent(event)
                                        }
                                    }
                                    false
                                }
                                .fillMaxSize()
                                .background(MaterialTheme.esColorScheme.bgPrimary)
                                .onFocusChanged { isFocused.value = it.isFocused }
                                .focusRequester(focusRequester)
                                .focusable()
                                .pointerInput(Unit) {
                                    detectTapGestures {
                                        focusRequester.requestFocus()
                                    }
                                },
                    ) {
                        Column(
                            modifier = Modifier.fillMaxSize(),
                        ) {
                            EnvBanner(isConnected = isConnected)

                            if (screen == null)
                                Navigator(SplashScreenNav) { navigator ->

                                    LaunchedEffect(Unit) {
                                        checkException(
                                            toastManager = toastManager,
                                            navigateToAuth = {
                                                navigator.popAll()
                                                navigator.push(AuthScreenNav)
                                            },
                                        )
                                    }

                                    LaunchedEffect(Unit) {
                                        delay(1000L)
                                        checkAuth(
                                            authRepository = authRepository,
                                            isLastItemAuth = { navigator.lastItem is AuthScreenNav },
                                            isLastItemSplash = { navigator.lastItem is SplashScreenNav },
                                            navigateToHome = {
                                                scope.launch {
                                                    configurationRepository
                                                        .getAllowedCurrencies()
                                                        .onSuccess { ac ->
                                                            ac?.let {
                                                                currencyRepository
                                                                    .getByCode(ac.default)
                                                                    .onSuccess { c ->
                                                                        CurrencyConfig.updateCode(code = c.code, symbol = c.symbol)
                                                                    }
                                                            }
                                                        }

                                                    dateFormatUseCase.setDateFormat()
                                                    featureRepository.getFeatures()
                                                        .onSuccess { features ->
                                                            featureRepository.getAllowedFeatures()
                                                                .onSuccess { allowedFeatures ->

                                                                    val matchedFeatures = features
                                                                        .filter { feature ->
                                                                            allowedFeatures.map { it.feature }
                                                                                .contains(feature.id)
                                                                        }.map { it.name }

                                                                    FeatureConfig.update(matchedFeatures)

                                                                    navigator.popAll()
                                                                    navigator.push(HomeScreenNav)
                                                                }
                                                        }
                                                }
                                            },
                                            navigateToAuth = {
                                                navigator.popAll()
                                                navigator.push(AuthScreenNav)
                                            },
                                        )
                                    }
                                    SlideTransition(navigator)
                                }
                            else
                                Navigator(screen) {
                                    SlideTransition(it)
                                }
                        }

                        if (isLoading.value)
                            LinearProgressIndicator(
                                color = MaterialTheme.esColorScheme.bgPrimary,
                                modifier = Modifier.fillMaxWidth(),
                            )
                    }

                    EsToastContainer(
                        toastManager = toastManager,
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(
                                end = 32.dp,
                            ),
                    )
                }
            }
        }
    }
}
